import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { MAJOR_PROJECTS, HOME_SERVICES } from "../../content/hubs";

const GROUPS = [
  { label: "Major Renovations", href: "/sc/major-projects/", items: MAJOR_PROJECTS, testid: "nav-major-projects" },
  { label: "Home Services", href: "/sc/home-services/", items: HOME_SERVICES, testid: "nav-home-services" },
];

const SIMPLE_LINKS = [
  { label: "How It Works", href: "/#how-it-works", testid: "nav-how-it-works" },
  { label: "Blog", href: "/blog/", testid: "nav-blog" },
  { label: "FAQ", href: "/#faq", testid: "nav-faq" },
];

const linkCls = "relative text-sm font-semibold text-[#173042] hover:text-[#133955] transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54BCE6] focus-visible:ring-offset-2 after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:w-0 after:bg-[#54BCE6] after:transition-all after:duration-300 hover:after:w-full";

const Dropdown = ({ group }) => (
  <div className="relative group" data-testid={group.testid}>
    <button
      type="button"
      aria-haspopup="true"
      className={`${linkCls} inline-flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0`}
    >
      {group.label}
      <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />
    </button>
    <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50">
      <div className="w-64 rounded-2xl bg-white shadow-[0_24px_60px_rgba(19,57,85,0.18)] border border-[rgba(19,57,85,0.08)] p-2">
        <a
          href={group.href}
          data-testid={`${group.testid}-all`}
          className="block rounded-xl px-4 py-2.5 text-sm font-bold text-[#133955] hover:bg-[#EAF7FC] transition-colors"
        >
          All {group.label} →
        </a>
        <div className="my-1.5 h-px bg-[rgba(19,57,85,0.08)]" />
        {group.items.map((item) => (
          <a
            key={item.slug}
            href={`${group.href.replace(/\/$/, "")}/${item.slug}/`}
            data-testid={`nav-dd-${item.slug}`}
            className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-[#173042]/80 hover:bg-[#EAF7FC] hover:text-[#133955] transition-colors"
          >
            {item.name}
          </a>
        ))}
      </div>
    </div>
  </div>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled ? "bg-white/90 backdrop-blur-xl border-b border-[rgba(19,57,85,0.08)]" : "bg-white/40 backdrop-blur-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-12 h-[84px] md:h-[92px] flex items-center justify-between">
          <Logo />

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {GROUPS.map((g) => <Dropdown key={g.label} group={g} />)}
            {SIMPLE_LINKS.map((l) => (
              <a key={l.href} href={l.href} data-testid={l.testid} className={linkCls}>
                {l.label}
              </a>
            ))}
            <a
              href="/login"
              data-testid="header-signin"
              className="rounded-full border-2 border-[#133955] text-[#133955] px-6 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#133955] focus-visible:ring-offset-2"
            >
              Sign In
            </a>
            <a
              href="/#request"
              data-testid="header-cta"
              className="rounded-full bg-[#54BCE6] text-[#133955] px-6 py-3 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#133955] focus-visible:ring-offset-2"
            >
              Request a Connection
            </a>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid place-items-center w-11 h-11 rounded-full bg-[#EAF7FC] text-[#133955] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54BCE6]"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden bg-white border-b border-[rgba(19,57,85,0.08)] max-h-[70vh] overflow-y-auto"
            >
              <div className="px-5 py-5 flex flex-col gap-1">
                {GROUPS.map((g) => (
                  <div key={g.label} className="border-b border-[#EAF7FC]">
                    <button
                      type="button"
                      data-testid={`mobile-${g.testid}`}
                      onClick={() => setExpanded((v) => (v === g.label ? null : g.label))}
                      aria-expanded={expanded === g.label}
                      className="w-full flex items-center justify-between py-2.5 text-base font-semibold text-[#133955]"
                    >
                      {g.label}
                      <ChevronDown size={16} className={`transition-transform duration-200 ${expanded === g.label ? "rotate-180" : ""}`} />
                    </button>
                    {expanded === g.label && (
                      <div className="pb-3 pl-3 flex flex-col gap-0.5">
                        <a href={g.href} onClick={() => setOpen(false)} data-testid={`mobile-${g.testid}-all`} className="py-2 text-sm font-bold text-[#54BCE6]">
                          All {g.label} →
                        </a>
                        {g.items.map((item) => (
                          <a
                            key={item.slug}
                            href={`${g.href.replace(/\/$/, "")}/${item.slug}/`}
                            onClick={() => setOpen(false)}
                            data-testid={`mobile-nav-dd-${item.slug}`}
                            className="py-2 text-sm font-semibold text-[#173042]/75"
                          >
                            {item.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {SIMPLE_LINKS.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-${l.testid}`}
                    className="py-2.5 text-base font-semibold text-[#133955] border-b border-[#EAF7FC] last:border-0"
                  >
                    {l.label}
                  </a>
                ))}
                <a
                  href="/login"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-signin"
                  className="mt-2 inline-flex items-center justify-center rounded-full border-2 border-[#133955] text-[#133955] py-3 text-base font-bold hover:bg-[#133955] hover:text-white transition-colors"
                >
                  Sign In
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Mobile sticky bottom CTA */}
      <a
        href="/#request"
        data-testid="mobile-sticky-cta"
        className="lg:hidden fixed bottom-0 inset-x-0 z-50 flex items-center justify-center gap-2 bg-[#133955] text-white font-bold py-4 px-6 shadow-[0_-8px_24px_rgba(19,57,85,0.18)]"
      >
        Request a Connection
        <ArrowRight size={18} />
      </a>
    </>
  );
};
